import { useEffect, useRef, useCallback } from 'react';
import { useDesignStore } from '@/stores';
import { usePublishStore } from '@/stores/publishStore';
import { toast } from 'react-hot-toast';
import i18n from '@/lib/i18n';
import { isEndingDeadImpersonation } from '@/lib/impersonation-session';

interface UseAutoSaveOptions {
  /** Delay in ms before auto-saving (default: 3000) */
  delay?: number;
  /** Whether auto-save is enabled (default: true) */
  enabled?: boolean;
  /** Show toast on save (default: false for auto-save) */
  showToast?: boolean;
}

/**
 * Hook for auto-saving designs
 * Debounces saves to avoid excessive API calls
 */
export function useAutoSave(options: UseAutoSaveOptions = {}) {
  const { delay = 3000, enabled = true, showToast = false } = options;

  // Field selectors: the hook lives for the whole editor session and must
  // not re-render its host on every unrelated store change.
  const hasUnsavedChanges = useDesignStore((state) => state.hasUnsavedChanges);
  const isSaving = useDesignStore((state) => state.isSaving);
  const saveDesign = useDesignStore((state) => state.saveDesign);
  const designId = useDesignStore((state) => state.designId);
  const canvas = useDesignStore((state) => state.canvas);
  // Publishing owns the save sequence (see usePublishDesign): no timer save
  // may land after the status turned ACTIVE. Subscribed so the scheduling
  // effect re-runs — and its cleanup cancels a pending timer — when the
  // flag turns on.
  const isPublishing = usePublishStore((state) => state.isPublishing);
  // A channel job owns the listing while it runs; the server refuses writes
  // and the store skips them, so no timer is even scheduled.
  const publishRunning = useDesignStore((state) => state.publishRunning);
  // A save the server refused as invalid is not retried until the design
  // changes again: resending the same state would fail the same way.
  const changeVersion = useDesignStore((state) => state.changeVersion);
  const rejectedAtVersion = useDesignStore((state) => state.rejectedAtVersion);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSaveRef = useRef<number>(0);

  /**
   * Cancel pending auto-save
   */
  const cancelAutoSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  }, []);

  /**
   * Trigger manual save immediately
   */
  const saveNow = useCallback(async () => {
    cancelAutoSave();

    if (!canvas) return false;

    const result = await saveDesign();
    if (result) {
      lastSaveRef.current = Date.now();
      if (showToast) {
        toast.success(i18n.t('creator.toast.designSaved'));
      }
      return true;
    }

    // Check for sync errors after manual save (Stufe 1.2: Error-Visibility)
    const syncError = useDesignStore.getState().syncError;
    if (syncError) {
      toast.error(i18n.t('creator.toast.syncFailed'), { duration: 5000 });
    }
    return false;
  }, [canvas, saveDesign, cancelAutoSave, showToast]);

  /**
   * Schedule auto-save
   */
  const scheduleAutoSave = useCallback(() => {
    if (
      !enabled ||
      !hasUnsavedChanges ||
      isSaving ||
      isPublishing ||
      publishRunning ||
      rejectedAtVersion === changeVersion
    )
      return;

    cancelAutoSave();

    saveTimerRef.current = setTimeout(async () => {
      // Only save if there's still unsaved changes and no publish started
      // since the timer was set (both read at fire time). A failure lands in
      // syncError and is shown by the header's SaveStatus; no toast here.
      if (
        useDesignStore.getState().hasUnsavedChanges &&
        !usePublishStore.getState().isPublishing &&
        !useDesignStore.getState().publishRunning
      ) {
        const result = await saveDesign();
        if (result) {
          lastSaveRef.current = Date.now();
        }
      }
    }, delay);
  }, [
    enabled,
    hasUnsavedChanges,
    isSaving,
    isPublishing,
    publishRunning,
    rejectedAtVersion,
    changeVersion,
    delay,
    cancelAutoSave,
    saveDesign,
  ]);

  // Trigger auto-save when changes occur
  useEffect(() => {
    if (hasUnsavedChanges && designId && enabled) {
      scheduleAutoSave();
    }

    return () => cancelAutoSave();
  }, [hasUnsavedChanges, designId, enabled, scheduleAutoSave, cancelAutoSave]);

  // Cleanup on unmount
  useEffect(() => {
    return () => cancelAutoSave();
  }, [cancelAutoSave]);

  // Warn on page unload while work is pending. A running save counts too:
  // the request may still be in flight, and the step change in the header
  // starts saves without waiting for them. The store is read at event time
  // so the listener is registered once and never lags behind a render.
  // An impersonation tab ending on a dead token (expired, revoked) cannot
  // save anymore: prompting would only strand the admin on a tab whose
  // saves all fail.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isEndingDeadImpersonation()) return;
      const { hasUnsavedChanges, isSaving } = useDesignStore.getState();
      if (hasUnsavedChanges || isSaving) {
        e.preventDefault();
        // Modern browsers ignore custom messages
        e.returnValue = i18n.t('creator.toast.unsavedChanges');
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  return {
    /** Trigger immediate save */
    saveNow,
    /** Cancel pending auto-save */
    cancelAutoSave,
    /** Whether currently saving */
    isSaving,
    /** Whether there are unsaved changes */
    hasUnsavedChanges,
    /** Timestamp of last save */
    lastSaveTime: lastSaveRef.current,
  };
}
